// src/pages/SupportChat.js
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { io } from "socket.io-client";

const API_BASE = process.env.REACT_APP_API_BASE;
const socket = io(API_BASE, { transports: ["websocket"] });

const SupportChat = () => {
  const { email } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const chatRef = useRef(null);
  const roomId = email;

  /* ================================
      📡 FETCH + SOCKET LISTENERS
  =================================*/
  useEffect(() => {
    fetchMessages();
    socket.emit("joinRoom", { roomId });

    socket.on("receiveSupportMessage", (msg) => {
      setMessages((prev) => [...prev, msg]);
      scrollToBottom();
    });

    const interval = setInterval(fetchMessages, 10000);

    return () => {
      clearInterval(interval);
      socket.off("receiveSupportMessage");
      socket.disconnect();
    };
  }, [roomId]);

  /* ================================
      📄 LOAD CHAT HISTORY
  =================================*/
  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/support/chat/${roomId}`);
      setMessages(res.data || []);
      scrollToBottom();
    } catch (err) {
      console.error("❌ Error loading support chat:", err);
    }
  };

  /* ================================
      ✉️ SEND MESSAGE
  =================================*/
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;

    const messageObj = {
      roomId,
      sender: email,
      content: newMsg,
    };

    try {
      await axios.post(`${API_BASE}/api/support/chat/send`, messageObj);
      socket.emit("sendSupportMessage", messageObj);
      setNewMsg("");
      scrollToBottom();
    } catch (err) {
      console.error("❌ Error sending support message:", err);
    }
  };

  /* ================================
      🔽 AUTO SCROLL
  =================================*/
  const scrollToBottom = () => {
    setTimeout(() => {
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
    }, 200);
  };

  /* ================================
      🎨 UI (Grey + Clean)
  =================================*/
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow p-4 text-center font-bold text-xl text-gray-700">
        💬 Live Support Chat
      </div>

      {/* Messages */}
      <div
        ref={chatRef}
        className="flex-1 p-6 overflow-y-auto space-y-4 bg-gray-100"
      >
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${
              msg.sender === email ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`px-4 py-2 rounded-2xl shadow text-sm max-w-xs break-words ${
                msg.sender === email
                  ? "bg-blue-500 text-white"
                  : "bg-gray-300 text-gray-800"
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <form
        onSubmit={sendMessage}
        className="p-4 flex gap-2 bg-white shadow-inner"
      >
        <input
          type="text"
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          placeholder="Type your message…"
          className="flex-1 border border-gray-300 px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-full"
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default SupportChat;
